import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useStatsigClient } from "@statsig/react-bindings";

import { webRPC } from "@kikoff/proto/src/rpc";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";
import { camelToSnake } from "@kikoff/utils/src/string";

import { AppThunk, RootState } from "@store";

import { createLoadableSelector, thunk } from "../utils";

const initialState = {
  variantByName: {} as Partial<Experiment.VariantByName>,
};

export type ExperimentState = typeof initialState;

const ExperimentSlice = createSlice({
  name: "Experiment",
  initialState,
  reducers: {
    updateVariantByName(
      state,
      { payload }: PayloadAction<ExperimentState["variantByName"]>
    ) {
      Object.assign(state.variantByName, payload);
    },
  },
});

const { actions } = ExperimentSlice;
export const {} = actions;
export default ExperimentSlice.reducer;

type ExperimentSelector<Variant extends "default" | "loadable"> = <
  Name extends Experiment.Name
>(
  name: Name
) => (
  state: RootState
) => {
  default: Experiment.VariantByName[Name] | Experiment.Control;
  loadable: [
    value: Experiment.VariantByName[Name] | Experiment.Control,
    loading: boolean
  ];
}[Variant];

type ExperimentThunk = <Name extends Experiment.Name>(
  name: Name
) => AppThunk<Promise<Experiment.VariantByName[Name]>>;

export const selectExperimentVariant = createLoadableSelector(
  <Name extends Experiment.Name>(name: Name) => (
    state: RootState
  ): Experiment.VariantByName[Name] | Experiment.Control =>
    state.experiment.variantByName[name] ?? "control",
  {
    loadAction: (name: Experiment.Name) => fetchExperimentVariant(name),
    selectLoaded: (name: Experiment.Name) => (state: RootState) =>
      name in state.experiment.variantByName,
  }
) as ExperimentSelector<"default"> & {
  load: ExperimentSelector<"loadable"> & {
    after: (condition: unknown) => ExperimentSelector<"loadable">;
  };
  loadAction: ExperimentThunk & {
    ifMissing: ExperimentThunk;
  };
};

export const fetchExperimentVariant = <Name extends Experiment.Name>(
  name: Name
) =>
  thunk((dispatch) => {
    if (
      !process.env.REALLY_PRODUCTION &&
      window &&
      new URLSearchParams(window.location.search).get(name)
    ) {
      const variant = new URLSearchParams(window.location.search).get(
        name
      ) as Experiment.VariantByName[Name];
      dispatch(actions.updateVariantByName({ [name]: variant }));
      return Promise.resolve(variant);
    }

    return webRPC.Experiments.getExperimentVariant({
      experimentNames: [name],
    }).then(
      handleProtoStatus({
        SUCCESS(data) {
          const variant = data.experimentVariants[
            name
          ] as Experiment.VariantByName[Name];
          dispatch(actions.updateVariantByName({ [name]: variant }));
          return variant;
        },
        _DEFAULT: handleFailedStatus("Failed to load experiment variant."),
      })
    );
  });

export const useExperiment = <Name extends Experiment.Statsig.Name>(
  name: Name
) => {
  const { getExperiment } = useStatsigClient();
  const experiment = getExperiment(camelToSnake(name));

  return {
    get: <Param extends keyof Experiment.Statsig.ParamByName[Name]>(
      param: Param,
      fallback: Experiment.Statsig.ParamByName[Name][Param]
    ) => experiment.get(camelToSnake(param.toString()), fallback),
  };
};

export namespace Experiment {
  export type Name = keyof VariantByName;
  export type Control = "control";

  export interface VariantByName {
    cblOnboardingV2Experiment:
      | "control"
      | "hybrid_credit_builder"
      | "smart_savings_builder";
    nonCreditLineSubscriptions: "small_entrypoint" | "large_entrypoint";
    onboardingCopy: "credit_plus" | "membership";
    engagementTodosV2: "control" | "candidate";
    nauxPartTwoExperiment: "naux_control" | "naux_treatment" | "control";
    nauxChargedOffV2Experiment:
      | "pay_off_full"
      | "pay_off_or_resubscribe"
      | "naux_control"
      | "control";
    nauxExpiredExperiment: "control" | "treatment";
    marketplaceHoldout: "control" | "holdout";
    salesOpsDowngradeExperiment: "control" | "treatment";
    ssnInputValidationExperiment:
      | "control"
      | "double_entry"
      | "confirmation_dialog";
    marketplaceLoansExperiment: "control" | "treatment";
    todoCardImpactExperiment:
      | "control"
      | "todos_above_ca"
      | "ca_and_upsell_above_todos" // treating this as control since there's no upsell carousel on web
      | "remove_todos";
    autoLoansCalculatorEntrypointExperiment: "control" | "treatment";
    marketplaceKatapultExperiment: "control" | "treatment";
    cblToPayCaExperiment: "control" | "candidate";
    preFurnishingExperience: "control" | "footer_view" | "pill_view";
    statsigSdkIntegration: "test";
  }

  export namespace Statsig {
    export type Name = keyof ParamByName;

    export interface ParamByName {
      efeTestIntegrationExperiment: {
        allowSubmit: boolean;
      };
    }
  }
}
